import type { ClipboardEventData } from '@ckeditor/ckeditor5-clipboard'
import type { Content3DX, MediaDescriptionContent3DX } from '@ds/swymkit/typings'

import {
  getSupportedMedias,
} from '~/entities/content/media/api/medias'
import { type IMedia, MEDIA_UPLOAD_STATUS, type UploadedMedia } from '~/entities/content/media/interfaces/media'

import { getPlatformId } from '~/features-shared/config-manager/stores/swym-config'
import { readFileAsText } from '~/features-shared/libs/utils/file-utils'
import { logger } from '~/features-shared/libs/utils/logger'
import { getMediaType, getTmpMedia, uploadFileGeneric } from '~/features-shared/media-upload/utils/media-upload'

const X3D_CONTENT_PROTOCOL = '3DXContent'
const X3D_MEDIA_DESCRIPTION_CONTENT_FILENAME_BASE = '_3DXMediaDescriptionContentFile_'

export enum X3DContentObjectType {
  /** Token to be added to the 3DXContent file name to be specific about its content */
  DRIVE = 'Drive',
}

/**
 * Check if the content is a Content 3DX
 * @param { unknown } content - The content to check
 */
export function isContent3DX(content: unknown): content is Content3DX {
  return (
    (content as Content3DX).data !== undefined
    && (content as Content3DX).protocol !== undefined
    && (content as Content3DX).version !== undefined
  )
}

/**
 * Check if the file is a 3DX media description file
 * @param file The file to check
 * @param type The type of 3dxcontent (i.e. drive)
 * @returns true if it is a 3dx media description file, false otherwise
 */
export function isMediaDescriptionContent3DXFile(file: File, type?: X3DContentObjectType): boolean {
  if (!type)
    type = X3DContentObjectType.DRIVE
  return file?.name?.includes(`${X3D_MEDIA_DESCRIPTION_CONTENT_FILENAME_BASE}${type}_-`)
}

/**
 * Check if the media description is drive one
 * @param mediaDescription media description to check
 * @returns true if it is a drive media description , false otherwise
 */
export function isDriveMediaDescriptionContent3DX(mediaDescription: MediaDescriptionContent3DX) {
  return mediaDescription?.objectType === 'DriveFile' && mediaDescription?.serviceId === '3DDrive'
}

/**
 * Filter a list of media description to only keep those matching Drive files
 * @param mediaDescriptionList The list of media description to be filtered
 * @returns A list of drive media description that are files
 */
export function filterMediaDescriptionContent3DXList(mediaDescriptionList: MediaDescriptionContent3DX[]) {
  return mediaDescriptionList.filter((mediaDescription: MediaDescriptionContent3DX) => {
    if (isDriveMediaDescriptionContent3DX(mediaDescription))
      return true
    else
      return false
  })
}

/**
 * Check if the media description is from current tenant
 * @param mediaDescription media description to check
 * @returns true if media description is from current tenant, false otherwise
 */
export function isMediaDescriptionContent3DXFromSameTenant(mediaDescription: MediaDescriptionContent3DX) {
  const tenantId = getPlatformId()
  return tenantId && mediaDescription?.envId === tenantId
}

/**
 * Read the 3DX media description file
 * @param file The file to read
 * @returns a promise with the media description as a json or null.
 */
export async function getMediaDescriptionContent3DXFileContent(file: File): Promise<MediaDescriptionContent3DX | null> {
  let content: MediaDescriptionContent3DX | null = null
  // Retrieve preview url
  try {
    content = JSON.parse(await readFileAsText(file) as string) as MediaDescriptionContent3DX
  }
  catch (error) {
    logger.error(error)
  }
  return content
}

/**
 * Create a file from a 3DX media description
 * @param mediaDescriptionContent3DX The 3DX media description used to create a file from
 * @returns A file with the description as content
 */
export function createFileFromMediaDescriptionContent3DX(mediaDescriptionContent3DX: MediaDescriptionContent3DX) {
  let type: X3DContentObjectType | '' = ''
  if (isDriveMediaDescriptionContent3DX(mediaDescriptionContent3DX))
    type = X3DContentObjectType.DRIVE
  return new File([JSON.stringify(mediaDescriptionContent3DX)], `${X3D_MEDIA_DESCRIPTION_CONTENT_FILENAME_BASE}${type}_-${mediaDescriptionContent3DX.displayName}`)
}

/**
 * Extract 3DX content from a DataTransfer object
 * @param dataTransfer The source
 * @returns The 3DX content as a json or null
 */
export function get3DXContentFromDataTransfer(dataTransfer: DataTransfer | ClipboardEventData['dataTransfer'] | null): Content3DX | null {
  let content3DX: Content3DX | null = null
  try {
    const plainTextInfo = JSON.parse(dataTransfer?.getData('text') || '{}')
    if (plainTextInfo?.protocol === X3D_CONTENT_PROTOCOL)
      content3DX = plainTextInfo
  }
  catch (e) { /* We are probably in firefox, in DND scenario inside embedded media-->  fail silently') */ }

  return content3DX
}

/**
 * Extract 3DX content from a X3DContentId string
 * @param {string} X3DContentId
 * @returns {Content3DX | undefined} The 3DX content or undefined
 */
export function get3DXContentFromX3DContentId(X3DContentId: string): Content3DX | undefined {
  let content3DX: Content3DX | undefined

  try {
    content3DX = JSON.parse(X3DContentId) as unknown as Content3DX

    if (isContent3DX(content3DX))
      return content3DX
    else
      return undefined
  }
  catch {
    logger.error('Error JSON parsing')
  }

  return undefined
}

/**
 * Create files from a list of mediaDescriptionContent3DX
 * @param mediaDescriptionContent3DXList The source used to create files
 * @param filter (Optional) Object to describe what content should be filtered
 * @param filter.sameTenant (Optional) Filter content from tenants other than current one
 * @param filter.isDrive (Optional) Filter content not from drive service
 * @returns A list of files
 */
export function createFilesFromMediaDescription3DXContent(
  mediaDescriptionContent3DXList: MediaDescriptionContent3DX[],
  filter?: {
    sameTenant?: boolean
    isDrive?: boolean
  },
) {
  const files: File[] = []
  mediaDescriptionContent3DXList.forEach(async (item) => {
    let filterItem = false
    if (item.objectType === 'text/directory')
      filterItem = true

    if (filter) {
      if (filter?.sameTenant && !isMediaDescriptionContent3DXFromSameTenant(item))
        filterItem = true
      if (filter?.isDrive && !isDriveMediaDescriptionContent3DX(item))
        filterItem = true
    }

    if (!filterItem)
      files.push(createFileFromMediaDescriptionContent3DX(item))
  })
  return files
}

/**
 * Create files from a 3dx content
 * @param x3DContent The source used to create files
 * @param filter (Optional) Object to describe what content should be filtered
 * @param filter.sameTenant (Optional) Filter content from tenants other than current one
 * @param filter.isDrive (Optional) Filter content not from drive service
 * @returns A list of files
 */
export function createFilesFrom3DXContent(x3DContent: Content3DX, filter?: {
  sameTenant?: boolean
  isDrive?: boolean
}) {
  const items = x3DContent.data?.items || []
  return createFilesFromMediaDescription3DXContent(items, filter)
}

/**
 * Get the original media title from the 3DX media description filename which contains a specific token.
 * @param mediaDescriptionContent3DXFileName The source
 * @returns The original title
 */
export function getMediaDescriptionContent3DXOriginalTitle(mediaDescriptionContent3DXFileName: string) {
  return mediaDescriptionContent3DXFileName?.replaceAll(`${X3D_MEDIA_DESCRIPTION_CONTENT_FILENAME_BASE}${X3DContentObjectType.DRIVE}_-`, '')
    ?.replaceAll(`${X3D_MEDIA_DESCRIPTION_CONTENT_FILENAME_BASE}-`, '')
}

/**
 * Upload media from Drive from media description and community or conversation uri
 * @param threadId The community or conversation uri
 * @param mediaDescriptions A list of media description
 * @returns A list of media infos
 */
export async function publishDriveMedia(threadId: string, mediaDescriptions: MediaDescriptionContent3DX[]) {
  const mediaList: IMedia[] = []

  const supportedExt = (await getSupportedMedias()).result
  const uploadPromises = []
  for (const mediaDescription of mediaDescriptions) {
    const file = createFileFromMediaDescriptionContent3DX(mediaDescription)
    const mediaType = await getMediaType(file, supportedExt)
    const mediaTmp = getTmpMedia({ file, mediaType })
    mediaTmp.newMedia = true

    const uploadPromise = uploadFileGeneric({
      mediaTmp,
      communityUri: threadId,
      supportedExt,
      isPublished: true,
    })
      .then((result) => {
        if (result
          && (result as UploadedMedia).mediaTmp.upload_status !== MEDIA_UPLOAD_STATUS.KO
          && (result as UploadedMedia).media
        )
          mediaList.push((result as UploadedMedia).media as IMedia)
      })
    uploadPromises.push(uploadPromise)
  }

  return Promise.all(uploadPromises)
    .then(() => mediaList)
}
